import React, { useEffect } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
} from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
// Create a Map ID in Google Cloud Console → Map Management, then add to .env.local
const GOOGLE_MAP_ID = import.meta.env.VITE_GOOGLE_MAP_ID || "DEMO_MAP_ID";

// Accra city centre default
const ACCRA = { lat: 5.6037, lng: -0.187 };

interface GoogleMapViewProps {
  pickup?: { lat: number; lng: number; address: string };
  destination?: { lat: number; lng: number; address: string };
  interactive?: boolean;
}

// Inner component — needs to be a child of APIProvider to use useMap()
function MapMarkers({
  pickup,
  destination,
}: Pick<GoogleMapViewProps, "pickup" | "destination">) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    if (pickup && destination) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: pickup.lat, lng: pickup.lng });
      bounds.extend({ lat: destination.lat, lng: destination.lng });
      map.fitBounds(bounds, 60);
    } else if (pickup) {
      map.panTo({ lat: pickup.lat, lng: pickup.lng });
      map.setZoom(14);
    }
  }, [map, pickup, destination]);

  return (
    <>
      {pickup && (
        <AdvancedMarker position={{ lat: pickup.lat, lng: pickup.lng }}>
          <div className="flex flex-col items-center">
            <div className="bg-white px-2 py-1 rounded-lg shadow-md text-[10px] font-bold mb-1 border border-green-500">
              PICKUP
            </div>
            <MapPin className="w-8 h-8 text-green-500 fill-white" />
          </div>
        </AdvancedMarker>
      )}
      {destination && (
        <AdvancedMarker
          position={{ lat: destination.lat, lng: destination.lng }}
        >
          <div className="flex flex-col items-center">
            <div className="bg-white px-2 py-1 rounded-lg shadow-md text-[10px] font-bold mb-1 border border-yenko-yellow">
              DESTINATION
            </div>
            <MapPin className="w-8 h-8 text-yenko-yellow fill-white" />
          </div>
        </AdvancedMarker>
      )}
    </>
  );
}

export const GoogleMapView: React.FC<GoogleMapViewProps> = ({
  pickup,
  destination,
  interactive = true,
}) => {
  if (!GOOGLE_MAPS_KEY) {
    return (
      <div className="w-full h-full rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-gray-100 flex items-center justify-center text-center p-6">
        <p className="text-gray-500 font-medium">
          Google Maps Key Missing
          <br />
          <span className="text-xs">
            Add VITE_GOOGLE_MAPS_API_KEY to .env.local
          </span>
        </p>
      </div>
    );
  }

  const defaultCenter = pickup ? { lat: pickup.lat, lng: pickup.lng } : ACCRA;

  return (
    <div className="w-full h-full rounded-3xl overflow-hidden shadow-2xl border-4 border-white relative">
      <APIProvider apiKey={GOOGLE_MAPS_KEY}>
        <Map
          mapId={GOOGLE_MAP_ID}
          defaultCenter={defaultCenter}
          defaultZoom={12}
          gestureHandling={interactive ? "auto" : "none"}
          disableDefaultUI={!interactive}
          style={{ width: "100%", height: "100%" }}
        >
          <MapMarkers pickup={pickup} destination={destination} />
        </Map>
      </APIProvider>
    </div>
  );
};
